import { useCallback, useState } from 'react'

import { RequestLog } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout'
import { CopyButton } from '#components/CopyButton.js'
import { CopyServiceRequestButton } from '#components/CopyServiceRequestButton.js'
import {
  RequestLogFilters,
  useRequestLogFilters,
} from '#components/RequestLogFilters.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { EbolDetails } from './EbolDetails'
import { EbolList } from './EbolList'

const EbolView = () => {
  const [selectedEbol, setSelectedEbol] = useState<RequestLog | null>(null)
  const { filters, localFilters, setLocalFilters } = useRequestLogFilters()

  const {
    items: ebols,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<RequestLog>('request-logs', {
    type: 'shipment',
    ...filters,
    limit: 50,
  })

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  const handleEbolSelect = useCallback((ebol: RequestLog) => {
    setSelectedEbol(ebol)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedEbol(null)
  }, [])

  const sliderTitle = (
    <div className="flex items-center gap-4">
      <span>EBOL Details</span>
      {selectedEbol && (
        <>
          <CopyButton
            textToCopy={`${window.location.protocol}//${window.location.host}${window.location.pathname}?requestId=${selectedEbol.requestId}`}
          />
          {selectedEbol.tenantRequest?.content && (
            <CopyServiceRequestButton log={selectedEbol} />
          )}
        </>
      )}
    </div>
  )

  return (
    <>
      <AdminListLayout
        title="eBOLs"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No eBOLs found"
        filters={
          <RequestLogFilters
            isLoading={isLoading}
            onRefresh={refresh}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            statuses={['ok', 'error']}
            errorTypes={[
              'authentication',
              'external',
              'internal',
              'unknown',
              'unsupported',
              'validation',
            ]}
          />
        }
        listContent={
          ebols.length > 0 ? (
            <EbolList
              ebols={ebols}
              selectedId={selectedEbol?._id}
              onSelect={handleEbolSelect}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
            />
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedEbol}
        onClose={handleCloseDetails}
        title={sliderTitle}
      >
        <EbolDetails ebol={selectedEbol} />
      </Slider>
    </>
  )
}

export { EbolView }
